import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Brandmark } from '../components/Brandmark';
import stageMp4 from '../assets/login-stage.mp4';
import stageWebm from '../assets/login-stage.webm';
import stagePoster from '../assets/login-stage-poster.jpg';

// Nettoyage minimal pour un segment d'URL, SANS forcer la casse : un
// identifiant société existant peut avoir été créé avec des majuscules
// (comptes créés avant l'inscription en self-service), et la comparaison
// est sensible à la casse côté serveur. Contrairement au formulaire
// d'inscription (qui, lui, normalise en minuscules pour les NOUVELLES
// sociétés), ce champ sert à rejoindre une société déjà existante : on ne
// touche donc pas à la casse saisie par l'utilisateur.
function cleanForUrl(v: string): string {
  return v.trim().replace(/[\s/]+/g, '');
}

// Page d'accueil (/) : redirige automatiquement les sessions déjà
// authentifiées vers leur société, sinon propose de créer une société ou
// de rejoindre une société existante par son identifiant.
export function Landing() {
  const { state, ready } = useAuth();
  const navigate = useNavigate();
  const [tenantId, setTenantId] = useState('');
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoReady, setVideoReady] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return;

    const markReady = () => setVideoReady(true);
    video.addEventListener('loadeddata', markReady);
    if (video.readyState >= 2) markReady();

    // Le mobile (Safari/Chrome) bloque l'autoplay si la video n'est pas
    // REELLEMENT muette au niveau JS : l'attribut `muted` en JSX seul ne
    // suffit pas toujours au premier rendu (bug React connu). On le force ici.
    video.muted = true;
    video.defaultMuted = true;
    video.play().catch(() => { /* l'affiche reste visible, rien a faire */ });

    const onVisibility = () => {
      if (document.hidden) video.pause();
      else video.play().catch(() => {});
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      video.removeEventListener('loadeddata', markReady);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  if (ready && state.token && state.tenant) {
    return <Navigate to={`/${state.tenant}`} replace />;
  }

  function goToTenantLogin(e: React.FormEvent) {
    e.preventDefault();
    const clean = cleanForUrl(tenantId);
    if (clean) navigate(`/${clean}/login`);
  }

  return (
    <div className="login-view">
      <section className="stage">
        <video
          ref={videoRef}
          className={'stage-media' + (videoReady ? ' is-ready' : '')}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={stagePoster}
          aria-hidden="true"
        >
          <source src={stageWebm} type="video/webm" />
          <source src={stageMp4} type="video/mp4" />
        </video>
        <div className="stage-veil" aria-hidden="true"></div>
        <div className="stage-brand">
          <span className="mark"></span><span className="name">CaissePilot</span>
          <span className="sep"></span><span>Servicepro Solutions</span>
        </div>
        <div className="stage-pitch">
          <h1>Chaque caisse.<br/>Chaque client.<br/><em>Sous contrôle.</em></h1>
          <p>Suivez les caisses PM et GM du dépôt central jusqu'à chaque client, avec remises, retours, pertes
             et facturation centralisés — et une assistance par IA pour le comptage, toujours vérifiée avant
             d'être enregistrée.</p>
        </div>
        <div className="stage-foot">Plateforme multi-sociétés — chaque société dispose de son propre espace.</div>
      </section>
      <section className="formside">
        <div className="inner">
          <Brandmark subtitle="Servicepro Solutions · Emballage" />
          <div className="card">
            <h2>Accéder à CaissePilot</h2>
            <form onSubmit={goToTenantLogin}>
              <label htmlFor="tenantId">Identifiant société</label>
              <input
                id="tenantId"
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                placeholder="ex. servicepro"
                autoCapitalize="off"
                autoCorrect="off"
              />
              <div className="sub2">Respectez exactement la casse (majuscules/minuscules) de votre identifiant.</div>
              <div className="row" style={{ marginTop: '1rem' }}>
                <button type="submit" className="primary" style={{ width: '100%' }} disabled={!tenantId.trim()}>
                  Se connecter
                </button>
              </div>
            </form>
            <div className="sub2" style={{ marginTop: '1rem' }}>
              Pas encore de société ? <Link to="/signup">Créer votre société</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
